import React, { useState, useEffect, useMemo } from "react";
import {
  getPartnerNetworkReport,
  PartnerNetworkReportResponse,
  PartnerNetworkReportRow,
  AffiliateNetwork,
  AFFILIATE_NETWORKS,
  AFFILIATE_NETWORK_LABELS,
} from "../api/partnerNetworkReport";
import { Loader } from "../components/Loader";
import { Button } from "../components/Button";
import { formatInteger } from "../helpers/formatNumber";
import { ReactComponent as DownloadIcon } from "../assets/icons/download.svg";
import { ReactComponent as ChevronIcon } from "../assets/icons/arrow-down.svg";

type SortBy =
  | "transactionsDesc"
  | "transactionsAsc"
  | "viewsDesc"
  | "viewsAsc"
  | "nameAsc"
  | "nameDesc";

const SORT_LABELS: Record<SortBy, string> = {
  transactionsDesc: "Transactions ↓",
  transactionsAsc: "Transactions ↑",
  viewsDesc: "Profile views ↓",
  viewsAsc: "Profile views ↑",
  nameAsc: "Name A→Z",
  nameDesc: "Name Z→A",
};

const allNetworksRecord = (value: boolean): Record<AffiliateNetwork, boolean> => ({
  partnerAds: value,
  adTraction: value,
  daisyCon: value,
  tradeDoubler: value,
  cj: value,
  awin: value,
  impact: value,
});

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toISOString().slice(0, 10);
};

const csvEscape = (value: string): string => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const sortRows = (
  rows: PartnerNetworkReportRow[],
  sortBy: SortBy
): PartnerNetworkReportRow[] => {
  const sorted = [...rows];
  switch (sortBy) {
    case "transactionsDesc":
      sorted.sort(
        (a, b) =>
          b.transactionCount - a.transactionCount ||
          a.businessName.localeCompare(b.businessName)
      );
      break;
    case "transactionsAsc":
      sorted.sort(
        (a, b) =>
          a.transactionCount - b.transactionCount ||
          a.businessName.localeCompare(b.businessName)
      );
      break;
    case "viewsDesc":
      sorted.sort(
        (a, b) =>
          b.profileViews - a.profileViews ||
          a.businessName.localeCompare(b.businessName)
      );
      break;
    case "viewsAsc":
      sorted.sort(
        (a, b) =>
          a.profileViews - b.profileViews ||
          a.businessName.localeCompare(b.businessName)
      );
      break;
    case "nameAsc":
      sorted.sort((a, b) => a.businessName.localeCompare(b.businessName));
      break;
    case "nameDesc":
      sorted.sort((a, b) => b.businessName.localeCompare(a.businessName));
      break;
  }
  return sorted;
};

const PartnerNetworkReport: React.FC = () => {
  const [report, setReport] = useState<PartnerNetworkReportResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("transactionsDesc");
  const [expanded, setExpanded] = useState<Record<AffiliateNetwork, boolean>>(
    allNetworksRecord(false)
  );

  const toggleNetwork = (network: AffiliateNetwork) => {
    setExpanded((prev) => ({ ...prev, [network]: !prev[network] }));
  };

  const setAllExpanded = (value: boolean) => {
    setExpanded(allNetworksRecord(value));
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await getPartnerNetworkReport();
      setReport(response);
      setBackendUnavailable(false);
    } catch (err) {
      setBackendUnavailable(true);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const trimmedSearch = searchQuery.trim().toLowerCase();
  const searchActive = trimmedSearch.length > 0;

  const filteredByNetwork = useMemo(() => {
    if (!report) return null;
    const result = {} as Record<AffiliateNetwork, PartnerNetworkReportRow[]>;
    for (const network of AFFILIATE_NETWORKS) {
      const all = report.byNetwork[network] ?? [];
      const filtered = searchActive
        ? all.filter((r) =>
            (r.businessName ?? "").toLowerCase().includes(trimmedSearch)
          )
        : all;
      result[network] = sortRows(filtered, sortBy);
    }
    return result;
  }, [report, trimmedSearch, searchActive, sortBy]);

  useEffect(() => {
    if (!filteredByNetwork || !searchActive) return;
    setExpanded((prev) => {
      const next = { ...prev };
      for (const network of AFFILIATE_NETWORKS) {
        if (filteredByNetwork[network].length > 0 && !next[network]) {
          next[network] = true;
        }
      }
      return next;
    });
  }, [filteredByNetwork, searchActive]);

  const exportToCsv = () => {
    if (!report || !filteredByNetwork) return;

    const header = [
      "Network",
      "Business Name",
      "Start Date",
      "End Date",
      "Profile Views",
      "Transactions",
    ];
    const csvLines: string[] = [header.join(",")];

    for (const network of AFFILIATE_NETWORKS) {
      const rows = filteredByNetwork[network];
      csvLines.push(
        [csvEscape(AFFILIATE_NETWORK_LABELS[network]), "", "", "", "", ""].join(
          ","
        )
      );
      if (rows.length === 0) {
        csvLines.push(["", "No partners", "", "", "", ""].join(","));
        continue;
      }
      for (const row of rows) {
        csvLines.push(
          [
            "",
            csvEscape(row.businessName ?? ""),
            formatDate(row.startDate),
            formatDate(row.endDate),
            String(row.profileViews),
            String(row.transactionCount),
          ].join(",")
        );
      }
    }

    const blob = new Blob([csvLines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `partner-network-report-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex flex-col gap-4">
          <h2 className="font-medium">Partner Network Report</h2>
          <div className="text-sm text-gray-600">
            Partners grouped by affiliate network: business name, start and end
            date, profile views, and transaction count.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="ghost"
            onClick={() => setAllExpanded(true)}
            disabled={!report}
            className="whitespace-nowrap text-sm"
          >
            Expand all
          </Button>
          <Button
            variant="ghost"
            onClick={() => setAllExpanded(false)}
            disabled={!report}
            className="whitespace-nowrap text-sm"
          >
            Collapse all
          </Button>
          <Button
            variant="outline"
            onClick={exportToCsv}
            disabled={!report}
            className="gap-2 whitespace-nowrap"
          >
            <DownloadIcon className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {backendUnavailable && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-medium mb-1">
            Awaiting backend implementation
          </div>
          <div>
            The endpoint{" "}
            <code>POST /business-account/get-partner-network-report</code> is
            not yet available. The page is wired up and ready — see{" "}
            <code>docs/PARTNER_NETWORK_REPORT_BACKEND.md</code> for the backend
            specification.
          </div>
        </div>
      )}

      {report && (
        <div className="mb-6 flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[260px] max-w-xl">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by business name..."
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="sort-by"
                className="text-sm text-gray-600 whitespace-nowrap"
              >
                Sort by
              </label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                {(Object.keys(SORT_LABELS) as SortBy[]).map((key) => (
                  <option key={key} value={key}>
                    {SORT_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </div>
      )}

      {report &&
        AFFILIATE_NETWORKS.map((network) => {
          const totalCount = report.byNetwork[network]?.length ?? 0;
          const rows = filteredByNetwork?.[network] ?? [];
          const isOpen = expanded[network];
          return (
            <div
              key={network}
              className="mb-3 border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleNetwork(network)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-2">
                  <ChevronIcon
                    className={`w-4 h-4 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                  <span className="font-medium">
                    {AFFILIATE_NETWORK_LABELS[network]}
                  </span>
                  <span className="text-sm text-gray-500">
                    {searchActive
                      ? `(${rows.length} of ${totalCount} ${
                          totalCount === 1 ? "partner" : "partners"
                        })`
                      : `(${totalCount} ${
                          totalCount === 1 ? "partner" : "partners"
                        })`}
                  </span>
                </div>
              </button>
              {isOpen && (
                <div className="overflow-x-auto">
                  <table className="table-default">
                    <thead>
                      <tr>
                        <th className="min-w-[250px]">Business Name</th>
                        <th className="min-w-[120px]">Start Date</th>
                        <th className="min-w-[120px]">End Date</th>
                        <th className="min-w-[120px] text-right">
                          Profile Views
                        </th>
                        <th className="min-w-[120px] text-right">
                          Transactions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-gray-500 italic"
                          >
                            {searchActive
                              ? `No partners match "${searchQuery}"`
                              : "No partners"}
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.businessId}>
                            <td>{row.businessName}</td>
                            <td>{formatDate(row.startDate)}</td>
                            <td>{formatDate(row.endDate)}</td>
                            <td className="text-right">
                              {formatInteger(row.profileViews)}
                            </td>
                            <td className="text-right">
                              {formatInteger(row.transactionCount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default PartnerNetworkReport;
